<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Checkbox, Search } from "vant";
import { useMoment } from "@/stores/moment";
import { useMate } from "@/stores/mate";
import { Moment } from "@/types/entity";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import { ScrollLoadingListInstance } from "@/components/mobile/ScrollLoadingList.vue";
import BoxedTab from "@/components/mobile/common/BoxedTab.vue";
import i18n from "@/locales";
import ThreadList from "../ThreadList.vue";
// Props & Emit
withDefaults(
  defineProps<{
    include: number[];
    mateId?: number;
  }>(),
  {}
);

const emit = defineEmits<{
  (e: "select", moment: Moment): void;
}>();

const SORTER_ENUM: { text: string; value: "desc" | "asc" }[] = [
  { text: i18n.global.t("story.new"), value: "desc" },
  { text: i18n.global.t("story.old"), value: "asc" },
];

// State
const momentStore = useMoment();
const mateStore = useMate();
const searchInput = ref("");
const matchedText = ref("");
const momentList = ref<Moment[]>([]);
const scrollableEl = ref<HTMLElement | null>(null);
const filteredMomentList = computed(() => {
  // 1. 将数组分为多个数组
  const arrSplitByRootId = momentList.value.reduce((acc, cur) => {
    if (acc.length === 0) {
      acc.push([cur]);
      return acc;
    }
    if (cur.childId !== null) {
      acc[acc.length - 1].push(cur);
    } else {
      acc.push([cur]);
    }
    return acc;
  }, [] as Moment[][]);
  // 2. 对每个数组进行排序
  arrSplitByRootId.forEach((arr) => {
    arr.sort((a, b) =>
      a.rootId || b.rootId
        ? new Date(a.dtCreate!).getTime() - new Date(b.dtCreate!).getTime()
        : new Date(b.dtCreate!).getTime() - new Date(a.dtCreate!).getTime()
    );
  });
  // 3. 合并数组
  return arrSplitByRootId.flat().filter((item) => item.text);
});
const selectedSorterIdx = ref(0);
const listRef = ref<ScrollLoadingListInstance | null>(null);
//
watch(selectedSorterIdx, () => {
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
});
// EventHandler
const scrollLoad = async () => {
  let cursor = undefined;
  if (momentList.value.length > 0) {
    cursor = momentStore.useCursorEncodeByCreate(
      momentList.value[momentList.value.length - 1].dtCreate!,
      true
    );
  }

  const moments = await momentStore.fetchMomentByMateId(
    mateStore.userMate!.id,
    cursor,
    searchInput.value,
    undefined,
    {
      field: "dtCreate",
      order: SORTER_ENUM[selectedSorterIdx.value].value,
    }
  );
  if (moments.length === 0) return false;

  momentList.value.push(...moments);
  return true;
};
const onMomentSelect = (moment: Moment) => {
  emit("select", moment);
};
const onSearch = (val: string) => {
  matchedText.value = val;
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
};
const resetSearch = () => {
  matchedText.value = "";
  momentList.value = [];
  listRef.value?.resetScrollLoadingState();
};
</script>
<template>
  <div class="w-full overflow-hidden flex flex-col relative h-full">
    <div class="flex gap-1 items-center bg-base-200 pr-2">
      <search
        v-model="searchInput"
        class="flex-grow"
        :placeholder="$t('Message.keywordEnterSerach')"
        @clear="resetSearch"
        @search="onSearch"
      />
      <boxed-tab
        v-model:selected-idx="selectedSorterIdx"
        :disable="listRef?.scrollLoading"
      >
        <div v-for="(item, idx) in SORTER_ENUM" :key="idx">
          {{ item.text }}
        </div>
      </boxed-tab>
    </div>
    <div ref="scrollableEl" class="overflow-y-scroll w-full h-full">
      <ThreadList ref="listRef" :on-scroll-load="scrollLoad" class="min-h-full">
        <div>
          <div
            v-for="item in filteredMomentList"
            :key="item.uuid"
            :class="{
              'bg-base-300': include?.includes(item.id),
            }"
            class="flex gap-2 border-b px-4"
            @click="onMomentSelect(item)"
          >
            <div class="h-10 leading-10 pt-4">
              <Checkbox :model-value="include?.includes(item.id)" />
            </div>
            <div class="relative flex-grow">
              <thread-item
                :avatar-url="item.mate.mediaUrl"
                :chat-message-list="item.chatMessagezz"
                :content="item.text"
                :enable-audio-play="false"
                :enable-chat-message-click="false"
                :enable-reaction-btn="false"
                :enableTextCollapsed="false"
                :from-mate="item.fromMate"
                :has-next="item.childId != null"
                :has-prev="item.parentId != null"
                :impression-reactions="[]"
                :matched-text="matchedText"
                :mate-id="item.mate.uuid"
                :mate-tone-id="item.mate.mateToneId"
                :media-list="item.mediazz"
                :mention-matezz="item.mentionMatezz ?? []"
                :moment="item"
                :show-avatar="true"
                :show-comment="false"
                :show-reaction="false"
                :time="item.dtCreate"
                :title="item.mate.name"
                :type="item.mate.type"
                :uuid="item.uuid"
                :visibility="item.visibility"
                class="border-b-0"
              />
              <!-- 遮罩层 -->
              <div class="absolute top-0 right-0 left-0 bottom-0"></div>
            </div>
          </div>
        </div>
      </ThreadList>
    </div>
  </div>
</template>
